/**
 * Full-surface retry state for degraded-connectivity read failures (ISS-445).
 *
 * Rendered when a read could not reach the server AND nothing usable is
 * cached. Says what actually happened ("weak connection", not "not found" /
 * "no data"), offers a manual retry, and retries ITSELF when the shared
 * reachability monitor sees the connection recover — field techs' signal
 * comes and goes on its own, and Joel should not have to know to press a
 * button (ISS-445).
 */
import { useEffect, useRef } from 'react';
import { CloudOff } from 'lucide-react';
import { useReachability } from '@officehub/offline-sdk';
import { reachability } from '../../lib/reachability';
import { Button } from '../forms/Button';
import { Card } from './Card';

interface DegradedReadStateProps {
  /** Re-run the failed read (typically the query's `refetch`). */
  onRetry: () => void;
  /** True while the retry is in flight — disables the button. */
  isRetrying?: boolean;
  /** Heading. Keep it about the CONNECTION, never about the data. */
  title?: string;
  message?: string;
}

export function DegradedReadState({
  onRetry,
  isRetrying = false,
  title = "Can't reach the server",
  message = 'You seem to have a weak connection. Nothing was lost — your data is still there. ' +
    'Retry now, or this will retry on its own when the connection comes back.',
}: DegradedReadStateProps) {
  const state = useReachability(reachability);
  const prevStateRef = useRef(state);
  const onRetryRef = useRef(onRetry);
  onRetryRef.current = onRetry;

  useEffect(() => {
    const prev = prevStateRef.current;
    prevStateRef.current = state;
    if (prev !== 'online' && state === 'online') {
      onRetryRef.current();
    }
  }, [state]);

  return (
    <div className="flex items-center justify-center px-6 py-12">
      <Card padding="lg" className="w-full max-w-sm text-center">
        <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <CloudOff className="w-6 h-6 text-slate-500" aria-hidden="true" />
        </div>

        <h2 className="text-base font-black text-slate-900 mb-2">{title}</h2>
        <p className="text-sm text-slate-600 mb-5">{message}</p>

        <Button
          onClick={onRetry}
          loading={isRetrying}
          loadingText="Trying again..."
          fullWidth
        >
          Try again
        </Button>
      </Card>
    </div>
  );
}
